import { createApp } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import App from "./App.vue";
import { useAuth } from "./composables/useAuth";
import { allowsAny, routePermissions } from "./access";
import { homePathFor } from "./home";
import "./styles/tokens.css";
import "./styles/prototype.css";
import "./styles/ui.css";
// 文档编辑器样式：文件管理（新建/编辑文档）与任务详情共用（以前只写在文档中心页面里，别处用编辑器就没样式）
import "./styles/document.css";

const router = createRouter({
  history: createWebHistory(),
  routes: [
    // 根路径不写死 /dashboard：交给路由守卫按角色的「默认首页」决定（见 home.ts）
    { path: "/", name: "home", component: { template: "<span />" } },
    { path: "/login", name: "login", component: { template: "<span />" } },
    { path: "/dashboard", name: "dashboard", component: () => import("./views/core/DashboardView.vue") },
    { path: "/tasks", name: "tasks", component: () => import("./views/core/TasksView.vue") },
    // 风险事件已并入任务中心：老书签 / 外部链接落到任务中心，不要 404
    { path: "/events", redirect: { name: "tasks" } },
    { path: "/audit", name: "audit", component: () => import("./views/core/AuditView.vue") },
    { path: "/approvals", name: "approvals", component: () => import("./views/core/ApprovalsView.vue") },
    { path: "/risk", name: "risk", component: () => import("./views/risk/RiskCenterView.vue") },
    { path: "/coins", redirect: { path: "/risk", query: { tab: "coins" } } },
    { path: "/addresses", redirect: { path: "/risk", query: { tab: "addresses" } } },
    { path: "/exchanges", redirect: { path: "/risk", query: { tab: "exchanges" } } },
    { path: "/files", name: "files", component: () => import("./views/risk/FilesView.vue") },
    // 文档已并入文件管理：老书签 / 外部链接落到 /files，不要 404
    { path: "/documents", redirect: { name: "files" } },
    // 工资发放已集成进综合办公：老链接/书签保留，直接落到综合办公
    { path: "/payroll", redirect: { name: "office" } },
    { path: "/notices", name: "notifications", component: () => import("./views/notify/NotificationsView.vue") },
    { path: "/duty", name: "duty", component: () => import("./views/duty/DutyView.vue") },
    // 统计报表暂时下线（重新设计中）：页面代码/dev 里的实现都保留，这里只挂一个占位提示
    { path: "/reports", name: "reports", component: () => import("./views/admin/ReportOfflineView.vue") },
    { path: "/departments", redirect: { path: "/settings", query: { tab: "departments" } } },
    { path: "/roles", redirect: { path: "/settings", query: { tab: "roles" } } },
    { path: "/users", redirect: { path: "/settings", query: { tab: "users" } } },
    { path: "/permissions", redirect: { path: "/settings", query: { tab: "permissions" } } },
    { path: "/office", name: "office", component: () => import("./views/office/OfficeView.vue") },
    { path: "/settings", name: "settings", component: () => import("./views/admin/SettingsView.vue") },
    { path: "/me", name: "me", component: () => import("./views/admin/ProfileView.vue") },
    { path: "/account", name: "account", component: () => import("./views/admin/AccountView.vue") },
    { path: "/:pathMatch(.*)*", redirect: { name: "home" } }
  ]
});

function firstAllowed() {
  const auth = useAuth();
  return Object.keys(routePermissions).find(name => allowsAny(auth.state.user, routePermissions[name])) ?? "me";
}
router.beforeEach(async (to) => {
  const auth = useAuth();
  if (!auth.state.ready) await auth.restore();
  if (to.name === "login") return auth.signedIn.value ? { path: homePathFor(auth.state.user) } : true;
  if (!auth.signedIn.value) return { name: "login" };
  // 进入根路径 / 通配路径：按角色配置的默认首页打开
  if (to.name === "home") return { path: homePathFor(auth.state.user) };
  const needed = routePermissions[String(to.name ?? "")];
  if (needed && !allowsAny(auth.state.user, needed)) {
    const next = firstAllowed();
    if (to.name === next) return true;
    return { name: next };
  }
  return true;
});

createApp(App).use(router).mount("#app");
